// row 124 Collection and CollectionCard (contract C8; DC-236, DC-243): a grid of cards whose column count is the most of
// its `max` (2 or 3) that fit at a 340 px least card width in the collection's own width, and one column under an
// 877 px viewport. So the count answers the space the collection has, not the window: an open assistant panel, a wide
// navigation or a column beside it (the coverage validator) takes columns away by itself. A CollectionCard is a cell:
// it spans one column (a vertical card) or, as a band, every column. The flow is row dense and top-aligned (a card is
// as tall as its content); a track is never wider than the collection, so nothing scrolls sideways. The DOM order is
// the reading, focus and keyboard order (no CSS `order`); dense packing only lifts a later vertical card into a gap a
// band left. `grip` is a sortable handle (row 113 Grip, wired by the host's useSortable) drawn at the lead of the
// card's header row; the host owns the order. A cell whose card renders nothing (can={false}) takes no slot.

import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { vars } from "../lib/vars";

/** The most columns: 2 (the interview plan's rounds, DC-243) or 3 (a detail screen, DC-236). */
export type CollectionMax = 2 | 3;
/** one: a vertical card in one column; all: a band across every column. */
export type CollectionSpan = "one" | "all";

/** The least card width in px (C8); two columns need 2 × 340 + the gap. */
export const COLLECTION_MIN_CARD = 340;
/** Under this viewport width the collection is one column (C8). */
export const COLLECTION_ONE_COLUMN_BELOW = 877;

export interface CollectionProps {
  /** The most columns that may sit side by side. */
  max: CollectionMax;
  /** The accessible name of the group (his noun for what it holds); without it the collection is a plain box. */
  label?: string | undefined;
  /** Or the id of the heading that names the group. */
  labelledBy?: string | undefined;
  /** Gap between cards in px (default 14, the layout gap). */
  gap?: number | undefined;
  children?: ReactNode;
  hs?: Hs | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

/** #124: the collection. Its children are CollectionCards, in reading order. */
export function Collection({ max, label, labelledBy, gap, children, hs, id, className }: CollectionProps) {
  const hot = useHs(hs);
  const named = label !== undefined || labelledBy !== undefined;
  return (
    <div
      id={id}
      className={cx("mc-coll", className)}
      role={named ? "group" : undefined}
      aria-label={label}
      aria-labelledby={labelledBy}
      data-max={max}
      style={vars({ "--mc-cmax": max, "--mc-gap": gap })}
      {...hot}
    >
      {children}
    </div>
  );
}

export interface CollectionCardProps {
  /** one (default): a vertical card; all: a band across every column (a stepper, a form, a table, history). */
  span?: CollectionSpan | undefined;
  /** A sortable handle (the row 113 Grip) drawn at the lead of the card's header row; the host reorders. */
  grip?: ReactNode | undefined;
  /** false: the seat may not see this card; the cell is not drawn and takes no slot. */
  can?: boolean | undefined;
  /** The card (a Card or Card2, or a component that draws one). */
  children?: ReactNode;
  id?: string | undefined;
  /** E.g. the host's useSortable movedClass(key) for the settle after a move. */
  className?: string | undefined;
}

/** #124 ccard: one cell of a collection, holding its card. */
export function CollectionCard({ span = "one", grip, can, children, id, className }: CollectionCardProps) {
  if (can === false) return null;
  const gripped = grip !== undefined && grip !== null && grip !== false;
  return (
    <div id={id} className={cx("mc-ccard", span === "all" && "mc-ccard-all", gripped && "mc-ccard-g", className)} data-span={span}>
      {gripped ? <div className="mc-ccard-grip">{grip}</div> : null}
      {children}
    </div>
  );
}

/**
 * The columns a collection draws in a container `width` px wide (the CSS's own arithmetic, for tests and for hosts
 * that size something beside it): the most of `max` whose tracks stay at least 340 px with the gaps between them, at
 * least 1; and 1 under an 877 px viewport.
 */
export function collectionColumns(width: number, max: CollectionMax, viewport = Infinity, gap = 14): number {
  if (viewport < COLLECTION_ONE_COLUMN_BELOW) return 1;
  let columns = max;
  while (columns > 1 && columns * COLLECTION_MIN_CARD + (columns - 1) * gap > width) columns -= 1;
  return columns;
}
